// Builds the complete HTML document for a sheet of labels.
//
// The same function produces the on-screen preview and the printed document,
// so what staff see is what the printer receives. Preview mode only adds a
// screen stylesheet (slot numbers, used/empty stickers, page captions) and
// selectable slots; it never changes label markup or the sheet grid.

import {
  computeSheetMetrics,
  normalizeDeviceCalibration,
  normalizeSheetGeometry,
  planSheetLayout,
} from "./sheetLayout";
import { normalizeLabelContent } from "./barcodeLabelTemplate";
import { resolveLabelTemplate } from "./labelTemplates";

export const LABEL_SHEET_MODES = Object.freeze({ PRINT: "print", PREVIEW: "preview" });

const SHEET_CSS = `
    * { box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    html, body {
      margin: 0;
      padding: 0;
      width: var(--page-width);
      min-height: var(--page-height);
      background: #fff;
      color: var(--ink);
      font-family: "Helvetica Neue", Arial, sans-serif;
    }
    .print-page {
      width: var(--page-width);
      height: var(--page-height);
      padding: calc(var(--margin-top) + var(--print-offset-y)) 0 0 calc(var(--margin-left) + var(--print-offset-x));
      box-sizing: border-box;
      overflow: hidden;
      display: grid;
      /* Fixed millimeter tracks are more deterministic on physical printers than
         percentages, auto rows, viewport units, or flexible distribution. */
      grid-template-columns: repeat(var(--cols), var(--label-width));
      grid-template-rows: repeat(var(--rows), var(--label-height));
      row-gap: var(--label-row-gap);
      column-gap: var(--label-column-gap);
      align-content: start;
      justify-content: start;
      break-after: page;
      background: #fff;
    }

    /* Used (skipped) and unused stickers print nothing. */
    .sheet-slot {
      width: var(--label-width);
      height: var(--label-height);
    }
`;

// Screen-only decoration for the preview. Never part of the printed document.
const PREVIEW_CSS = `
    html, body { background: #e7e9ee; min-height: 0; }
    body { padding: 6mm 0 2mm; }
    .sheet-page-caption {
      width: var(--page-width);
      padding: 0 1mm 2mm;
      font: 600 11pt/1.2 "Helvetica Neue", Arial, sans-serif;
      color: #334155;
      display: flex;
      justify-content: space-between;
      gap: 4mm;
    }
    .sheet-page-caption span { font-weight: 500; color: #64748b; }
    .print-page {
      margin: 0 0 10mm;
      box-shadow: 0 1mm 4mm rgba(15, 23, 42, 0.18);
      position: relative;
    }
    .label-card, .sheet-slot { position: relative; }
    .sheet-slot {
      border-radius: 2.2mm;
      display: flex;
      align-items: center;
      justify-content: center;
      font: 700 16pt/1 "Helvetica Neue", Arial, sans-serif;
    }
    .sheet-slot::after { content: attr(data-position); }
    .sheet-slot--skipped {
      color: rgba(71, 85, 105, 0.55);
      background: repeating-linear-gradient(135deg, #eef0f4 0 2mm, #e2e6ec 2mm 4mm);
      border: 0.3mm solid #d5dae2;
    }
    .sheet-slot--empty {
      color: rgba(148, 163, 184, 0.75);
      border: 0.35mm dashed #cbd2dc;
    }
    .label-card::before {
      content: attr(data-position);
      position: absolute;
      top: 1mm;
      left: 1mm;
      min-width: 5mm;
      padding: 0.4mm 1mm;
      border-radius: 1.2mm;
      background: #111827;
      color: #fff;
      font: 700 7pt/1.2 "Helvetica Neue", Arial, sans-serif;
      text-align: center;
      z-index: 1;
    }
    [data-select-position] { cursor: pointer; }
    [data-select-position]:hover { outline: 0.6mm solid rgba(37, 99, 235, 0.45); outline-offset: 0.4mm; }
    .is-start { outline: 0.8mm solid #2563eb; outline-offset: 0.4mm; }
`;

const PRINT_SCRIPT = `
  <script>
    (function () {
      function go() { setTimeout(function () { window.print(); }, 80); }
      if (document.readyState === "complete" || document.readyState === "interactive") go();
      else document.addEventListener("DOMContentLoaded", go);
    })();
  </script>`;

const renderSlot = ({ slot, labels, isPreview, isFirstPage, startPosition, template, geometry }) => {
  const selectable = isPreview && isFirstPage;
  const isStart = selectable && slot.position === startPosition;
  const attributes = ` data-position="${slot.position}"${
    selectable ? ` data-select-position="${slot.position}"` : ""
  }`;

  if (slot.state === "label") {
    const entry = labels[slot.labelIndex];
    return template.renderCard(entry.label, entry.barcodeSvg, {
      attributes,
      className: isStart ? "is-start" : "",
      geometry,
    });
  }

  return `<div class="sheet-slot sheet-slot--${slot.state}${isStart ? " is-start" : ""}"${attributes}></div>`;
};

/**
 * @param {object} input
 * @param {Array<{label: object, barcodeSvg: string}>} input.labels in print order
 * @param {object} input.geometry sheet geometry (columns, rows, margins, gaps)
 * @param {object} [input.calibration] device offsets
 * @param {number} [input.startPosition] first sticker on page 1
 * @param {string} [input.templateId] label design; omitted = "footwear-classic", unknown throws
 * @param {"print"|"preview"} [input.mode]
 * @param {number} [input.previewPageLimit] preview only: pages to draw
 * @param {(page: object) => string} [input.pageCaption] preview only
 */
export const buildLabelSheetDocument = ({
  labels,
  geometry,
  calibration,
  appearance,
  templateId,
  startPosition = 1,
  mode = LABEL_SHEET_MODES.PRINT,
  previewPageLimit = Infinity,
  pageCaption,
}) => {
  const template = resolveLabelTemplate(templateId);
  const sheet = normalizeSheetGeometry(geometry);
  const offsets = normalizeDeviceCalibration(calibration);
  const metrics = computeSheetMetrics(sheet);
  const content = normalizeLabelContent(appearance);
  const isPreview = mode === LABEL_SHEET_MODES.PREVIEW;
  const plan = planSheetLayout({
    labelCount: labels.length,
    columns: sheet.columns,
    rows: sheet.rows,
    startPosition,
  });

  const pages = isPreview ? plan.pages.slice(0, previewPageLimit) : plan.pages;
  const pageHTML = pages
    .map((page) => {
      const cells = page.slots
        .map((slot) =>
          renderSlot({
            slot,
            labels,
            isPreview,
            isFirstPage: page.pageNumber === 1,
            startPosition,
            template,
            geometry: sheet,
          })
        )
        .join("");
      const caption =
        isPreview && pageCaption
          ? `<div class="sheet-page-caption">${pageCaption(page, plan)}</div>`
          : "";
      return `${caption}<section class="print-page" data-page="${page.pageNumber}">${cells}</section>`;
    })
    .join("");

  return {
    plan,
    html: `<!doctype html>
<html>
<head>
  <meta charset="utf-8" />
  <title>Catalog Barcode Labels</title>
  <style>
    :root {
      --cols: ${sheet.columns};
      --rows: ${sheet.rows};
      --page-width: ${metrics.paper.widthMM}mm;
      --page-height: ${metrics.paper.heightMM}mm;
      --margin-top: ${sheet.marginTopMM}mm;
      --margin-left: ${sheet.marginLeftMM}mm;
      --label-row-gap: ${sheet.labelRowGapMM}mm;
      --label-column-gap: ${sheet.labelColumnGapMM}mm;
      --label-width: ${metrics.labelWidthMM}mm;
      --label-height: ${metrics.labelHeightMM}mm;
      --padding-top: ${sheet.paddingTopMM}mm;
      --padding-right: ${sheet.paddingRightMM}mm;
      --padding-bottom: ${sheet.paddingBottomMM}mm;
      --padding-left: ${sheet.paddingLeftMM}mm;
      --title-size: ${content.title.fontSizePt}pt;
      --title-weight: ${content.title.fontWeight};
      --title-align: ${content.title.align};
      --title-lines: ${content.title.maxLines};
      --title-overflow: ${content.title.overflow === "ellipsis" ? "ellipsis" : "clip"};
      --color-size: ${content.color.fontSizePt}pt;
      --color-weight: ${content.color.fontWeight};
      --color-align: ${content.color.align};
      --color-justify: ${content.color.align === "left" ? "flex-start" : content.color.align === "right" ? "flex-end" : "center"};
      --size-size: ${content.size.fontSizePt}pt;
      --size-weight: ${content.size.fontWeight};
      --size-align: ${content.size.align};
      --price-size: ${content.price.fontSizePt}pt;
      --price-weight: ${content.price.fontWeight};
      --price-align: ${content.price.align};
      --barcode-height: ${content.barcode.heightMM}mm;
      --barcode-width: ${content.barcode.widthPercent}%;
      --barcode-side-pad: ${content.barcode.sidePaddingMM}mm;
      --barcode-text-size: ${content.barcode.textSizePt}pt;
      --barcode-text-display: ${content.barcode.showText ? "block" : "none"};
      --barcode-spacing: ${content.barcode.spacingMM}mm;
      --print-offset-x: ${offsets.printOffsetXMM}mm;
      --print-offset-y: ${offsets.printOffsetYMM}mm;
      --ink: #111111;
      --muted: rgba(17, 17, 17, 0.58);
      --soft-line: rgba(17, 17, 17, 0.12);
      --panel: #f7f4ee;
    }
${SHEET_CSS}
${template.cardCss}
    @page { size: ${metrics.paper.widthMM}mm ${metrics.paper.heightMM}mm; margin: 0; }
  </style>${isPreview ? `\n  <style media="screen">${PREVIEW_CSS}</style>` : ""}
</head>
<body>
  ${pageHTML}${isPreview ? "" : PRINT_SCRIPT}
</body>
</html>`,
  };
};
